import { useEffect, useRef, useState, useCallback } from "react";
import { Play, Pause, Volume2, VolumeX, X, ChevronLeft, ChevronRight, Film } from "lucide-react";

const REELS = [
  { id: "r1", src: "/uploads/videos/reels-1.mp4" },
  { id: "r2", src: "/uploads/videos/reels-2.mp4" },
  { id: "r3", src: "/uploads/videos/reels-3.mp4" },
  { id: "r4", src: "/uploads/videos/reels-4.mp4" },
  { id: "r5", src: "/uploads/videos/reels-5.mp4" },
];

export default function InstagramReels() {
  const trackRef = useRef(null);
  const videoRefs = useRef({}); // id -> HTMLVideoElement
  const observerRef = useRef(null);
  const [activeModalIdx, setActiveModalIdx] = useState(null);
  const [modalMuted, setModalMuted] = useState(false);
  const [modalPlaying, setModalPlaying] = useState(true);
  const modalVideoRef = useRef(null);

  // IntersectionObserver: play autoplay+muted only when card is in viewport
  useEffect(() => {
    if (!("IntersectionObserver" in window)) return;
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          if (entry.isIntersecting && entry.intersectionRatio >= 0.3) {
            const p = video.play();
            if (p && typeof p.catch === "function") p.catch(() => {});
          } else {
            video.pause();
          }
        });
      },
      { threshold: [0, 0.3, 0.75] }
    );
    Object.values(videoRefs.current).forEach((v) => v && observerRef.current.observe(v));
    return () => observerRef.current && observerRef.current.disconnect();
  }, []);

  const scroll = useCallback((dir) => {
    const el = trackRef.current;
    if (!el) return;
    const card = el.querySelector("[data-reel-card]");
    const step = card ? card.getBoundingClientRect().width + 16 : 280;
    el.scrollBy({ left: dir === "next" ? step : -step, behavior: "smooth" });
  }, []);

  const openModal = useCallback((idx) => {
    // pause autoplay thumbs when modal opens
    Object.values(videoRefs.current).forEach((v) => v && v.pause());
    setActiveModalIdx(idx);
    setModalMuted(false);
    setModalPlaying(true);
    document.body.style.overflow = "hidden";
  }, []);

  const closeModal = useCallback(() => {
    setActiveModalIdx(null);
    document.body.style.overflow = "";
    // resume autoplay on cards that come back to view
    setTimeout(() => {
      Object.values(videoRefs.current).forEach((v) => {
        if (v) {
          const rect = v.getBoundingClientRect();
          const inView = rect.top < window.innerHeight && rect.bottom > 0;
          if (inView) {
            const p = v.play();
            if (p && typeof p.catch === "function") p.catch(() => {});
          }
        }
      });
    }, 50);
  }, []);

  const modalPrev = useCallback(() => setActiveModalIdx((i) => (i === null ? null : (i - 1 + REELS.length) % REELS.length)), []);
  const modalNext = useCallback(() => setActiveModalIdx((i) => (i === null ? null : (i + 1) % REELS.length)), []);

  // Keyboard nav in modal
  useEffect(() => {
    if (activeModalIdx === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeModal();
      else if (e.key === "ArrowLeft") modalPrev();
      else if (e.key === "ArrowRight") modalNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [activeModalIdx, closeModal, modalPrev, modalNext]);

  const toggleModalPlay = () => {
    const v = modalVideoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play().catch(() => {});
      setModalPlaying(true);
    } else {
      v.pause();
      setModalPlaying(false);
    }
  };

  return (
    <section
      id="nossas-historias"
      data-testid="nossas-historias-section"
      className="section-pad bg-white relative overflow-hidden"
    >
      <div className="absolute -top-10 right-0 w-[28rem] h-[28rem] rounded-full bg-ga-emerald/8 blur-3xl pointer-events-none" />
      <div className="container-ga relative" data-testid="instagram-reels">
        <div className="flex items-end justify-between mb-8 gap-4 flex-wrap">
          <div className="max-w-2xl">
            <div className="pill border-ga-emerald/30 text-ga-emerald bg-ga-emerald/10 mb-4">
              <Film className="w-3.5 h-3.5" /> Nossas histórias em vídeo
            </div>
            <h2 className="font-display font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[1.02] text-ga-ink">
              Nossas <span className="gradient-text-ga">Histórias</span>.
            </h2>
            <p className="mt-4 text-ga-ink/65 text-lg leading-relaxed">
              Conheça de perto o impacto do Instituto na vida das crianças e famílias.
            </p>
          </div>
          <div className="hidden sm:flex items-center gap-2">
            <button
              type="button"
              onClick={() => scroll("prev")}
              aria-label="Vídeo anterior"
              data-testid="reels-nav-prev"
              className="w-11 h-11 rounded-full bg-white border-2 border-border text-ga-ink hover:bg-ga-ink hover:text-white hover:border-ga-ink transition-colors flex items-center justify-center"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              type="button"
              onClick={() => scroll("next")}
              aria-label="Próximo vídeo"
              data-testid="reels-nav-next"
              className="w-11 h-11 rounded-full bg-white border-2 border-border text-ga-ink hover:bg-ga-ink hover:text-white hover:border-ga-ink transition-colors flex items-center justify-center"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div
          ref={trackRef}
          className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-4 scrollbar-thin scrollbar-thumb-ga-ink/20"
          data-testid="reels-track"
        >
        {REELS.map((reel, idx) => (
          <button
            key={reel.id}
            type="button"
            data-reel-card
            onClick={() => openModal(idx)}
            data-testid={`reel-card-${reel.id}`}
            className="group relative shrink-0 snap-start block w-[70%] xs:w-[55%] sm:w-[40%] md:w-[28%] lg:w-[22%] aspect-[9/16] rounded-2xl overflow-hidden bg-ga-ink shadow-lg focus:outline-none focus:ring-4 focus:ring-ga-emerald/40"
          >
            <video
              ref={(el) => { if (el) videoRefs.current[reel.id] = el; }}
              src={reel.src}
              muted
              loop
              playsInline
              preload="metadata"
              poster=""
              className="absolute inset-0 w-full h-full object-cover"
            />
            {/* overlay + play glyph (institucional, sem branding de rede social) */}
            <div className="absolute inset-0 bg-gradient-to-t from-ga-ink/70 via-transparent to-transparent opacity-80 group-hover:opacity-60 transition-opacity" />
            <div className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/15 backdrop-blur-md flex items-center justify-center pointer-events-none">
              <VolumeX className="w-3.5 h-3.5 text-white/90" />
            </div>
            <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between text-white pointer-events-none">
              <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold bg-white/15 backdrop-blur-md rounded-full px-2.5 py-1">
                <Play className="w-2.5 h-2.5 fill-current" />
                Assistir
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* ---------- MODAL ---------- */}
      {activeModalIdx !== null && (
        <div
          className="fixed inset-0 z-50 bg-ga-ink/95 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={closeModal}
          data-testid="reels-modal"
        >
          <button
            type="button"
            onClick={closeModal}
            aria-label="Fechar"
            data-testid="reels-modal-close"
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
          >
            <X className="w-5 h-5" />
          </button>

          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); modalPrev(); }}
            aria-label="Vídeo anterior"
            data-testid="reels-modal-prev"
            className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); modalNext(); }}
            aria-label="Próximo vídeo"
            data-testid="reels-modal-next"
            className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
          >
            <ChevronRight className="w-6 h-6" />
          </button>

          <div
            className="relative w-full max-w-[420px] aspect-[9/16] bg-ga-ink rounded-2xl overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <video
              ref={modalVideoRef}
              key={REELS[activeModalIdx].id}
              src={REELS[activeModalIdx].src}
              autoPlay
              loop
              playsInline
              muted={modalMuted}
              controls={false}
              onClick={toggleModalPlay}
              className="absolute inset-0 w-full h-full object-contain bg-black cursor-pointer"
              data-testid="reels-modal-video"
            />

            <div className="absolute bottom-3 left-3 right-3 flex items-center gap-2 text-white">
              <button
                type="button"
                onClick={toggleModalPlay}
                aria-label={modalPlaying ? "Pausar" : "Reproduzir"}
                data-testid="reels-modal-playpause"
                className="w-9 h-9 rounded-full bg-white/15 backdrop-blur-md hover:bg-white/25 flex items-center justify-center"
              >
                {modalPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 fill-current" />}
              </button>
              <button
                type="button"
                onClick={() => setModalMuted((m) => !m)}
                aria-label={modalMuted ? "Ativar som" : "Desativar som"}
                data-testid="reels-modal-mute"
                className="w-9 h-9 rounded-full bg-white/15 backdrop-blur-md hover:bg-white/25 flex items-center justify-center"
              >
                {modalMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
              </button>
              <div className="ml-auto text-[11px] bg-white/15 backdrop-blur-md rounded-full px-2.5 py-1">
                {activeModalIdx + 1} / {REELS.length}
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </section>
  );
}
